<template lang="pug">
.metric-card(:class="{ 'is-loading': loading }")
  .metric-header
    .metric-title {{ title }}
    aiq-tooltip(v-if="tooltip" placement="top" :content="tooltip")
      fa-icon.metric-info(:icon="['fas', 'circle-question']")
  .metric-body
    span.metric-value(v-if="!loading") {{ formattedValue }}
    span.metric-loading(v-else)
      i.el-icon-loading
</template>

<script>
import { isNil } from 'lodash';
import pluralize from 'pluralize';
import dayjs from '@/localPlugins/dayjs';

const SCORE_DECIMAL_PLACES = 1;
const PERCENT_DECIMAL_PLACES = 2;

export default {
  name: 'MetricCard',
  props: {
    title: {
      type: String,
      required: true,
    },
    value: {
      type: Number,
      default: null,
    },
    metricCategory: {
      type: String,
      default: 'count',
    },
    metricId: {
      type: String,
      default: '',
    },
    tooltip: {
      type: String,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    formattedValue() {
      if (isNil(this.value)) {
        return 'N/A';
      }

      const numValue = this.value;

      switch (this.metricCategory) {
        case 'percent':
          return `${parseFloat(Number(numValue).toFixed(PERCENT_DECIMAL_PLACES))}%`;
        case 'timedelta':
        case 'interval':
          return this.metricId === 'relationship_length'
            ? this.formatRelationshipDuration(Number(numValue))
            : this.formatTimeDelta(Number(numValue));
        case 'days':
          return this.formatDays(numValue);
        case 'score':
          return Number(numValue).toFixed(SCORE_DECIMAL_PLACES);
        case 'currency':
          return this.formatCurrency(numValue);
        case 'count':
        default:
          return this.formatNumber(numValue);
      }
    },
  },
  methods: {
    formatTimeDelta(seconds) {
      if (!seconds || seconds < 0) return 'N/A';
      const duration = dayjs.duration(seconds, 'seconds');
      return [[Math.floor(duration.asDays()), 'days'], [duration.hours(), 'h'], [duration.minutes(), 'm'], [duration.seconds(), 's']]
        .filter(([value]) => value > 0)
        .map(([value, unit]) => `${value}${unit}`)
        .join(' ') || 'N/A';
    },
    formatRelationshipDuration(seconds) {
      if (!seconds || seconds < 0) return 'N/A';
      const duration = dayjs.duration(seconds, 'seconds');
      const years = duration.years();
      const months = duration.months();
      const days = duration.days();
      return [[years, 'year'], [months, 'month'], [days, 'day']]
        .filter(([value]) => value > 0)
        .map(([value, unit]) => `${value} ${pluralize(unit, value)}`)
        .join(' ') || 'N/A';
    },
    formatDays(seconds) {
      if (!seconds || seconds < 0) return '0d';
      const days = Math.round(dayjs.duration(seconds, 'seconds').asDays());
      return `${days}d`;
    },
    formatNumber(num) {
      if (typeof num !== 'number') return num;
      return num.toLocaleString();
    },
    formatCurrency(num) {
      if (typeof num !== 'number') return num;
      return num.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../styles/aiq-variables.scss";

.metric-card {
  background: $aiq-bgc-list-item;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  overflow: hidden;
  min-width: 140px;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  &.is-loading {
    opacity: 0.7;
  }

  .metric-loading {
    font-size: 22px;
    color: #409eff;
    display: inline-block;
  }
}

.metric-header {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  padding: 8px 10px;
  min-height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}

.metric-info {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.65);
  flex-shrink: 0;

  &:hover {
    color: #fff;
  }
}

.metric-title {
  font-size: 10px;
  font-weight: 600;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  line-height: 1.3;
  text-align: center;
}

.metric-body {
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60px;
}

.metric-value {
  font-size: 24px;
  font-weight: 700;
  color: $aiq-color-text-primary;
  line-height: 1;
}
</style>

<style lang="scss">
@import "../../../../styles/aiq-variables-dark.scss";

[data-theme="dark"] .metric-card {
  background: $aiq-bgc-sidebar-item-hover;
  border-color: $aiq-bdc-input-default;

  .metric-header {
    background: linear-gradient(135deg, #1a252f 0%, #2c3e50 100%);
  }

  .metric-value {
    color: $aiq-color-black;
  }
}
</style>
